import { EventBus, EVENTS } from '../../../shared/EventBus';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { getMotionPrefs } from '../accessibility/motionPrefs';
import { COMBAT_MOOD } from '../../../config/atmosphere/combatMood.config';
import { CombatMoodModel } from './CombatMoodModel';

/**
 * CombatMoodDriver — a reusable, fully-decoupled bridge that makes the scene
 * atmosphere react to combat:
 *
 *   EVENTS.ABILITY_CASTED  (a presentation signal — NOT netcode/damage, per the
 *     decouple-visuals rule)  →  cast-heat accumulator (CombatMoodModel)  →
 *   EVENTS.COMBAT_MOOD  →  AtmosphereLayer multiplies it onto the per-level mood.
 *
 * So the ambient lights/density swell while a fight is hot and ease back in a
 * lull. Works on ANY map with a mood-reactive preset (arena today, biomes next)
 * and is harmless on quiet maps (no casts → mood stays 1). Reduce-motion holds
 * it calm (and the modulation ticker is off there anyway). Client-presentation
 * only — zero server/sim impact, MP-safe.
 */
class CombatMoodDriver {
  private model = new CombatMoodModel();
  private unsubCast: (() => void) | null = null;
  private unsubTick: (() => void) | null = null;
  private lastEmitted = 1;
  private started = false;

  /** Idempotent. No-op when disabled in config. */
  start(): void {
    if (this.started || !COMBAT_MOOD.enabled) return;
    this.started = true;
    this.unsubCast = EventBus.getInstance().on(EVENTS.ABILITY_CASTED, () => this.model.onCast());
    this.unsubTick = PixiOverlayHost.addTickListener((dtMs: number) => this.tick(dtMs));
  }

  private tick(dtMs: number): void {
    this.model.tick(dtMs);
    // Reduce-motion → hold the scene calm (no combat pulse). The atmosphere
    // modulation ticker is already off under reduce-motion, so this just avoids
    // emitting churn that would do nothing.
    const next = getMotionPrefs().reduceMotion ? 1 : this.model.value();
    if (Math.abs(next - this.lastEmitted) > 0.01) {
      this.lastEmitted = next;
      EventBus.getInstance().emit(EVENTS.COMBAT_MOOD, { intensity: next });
    }
  }

  stop(): void {
    this.unsubCast?.();
    this.unsubCast = null;
    this.unsubTick?.();
    this.unsubTick = null;
    this.started = false;
  }
}

/** Process singleton — one combat-mood bridge for the client. */
export const combatMoodDriver = new CombatMoodDriver();

/** Start the combat-mood bridge (called once at game bootstrap). Idempotent. */
export function startCombatMoodDriver(): void {
  combatMoodDriver.start();
}
